import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { badNameChars, NAME_CHARS } from "./subscriberCreate";

/**
 * Users › User diagnose and Devices › Change ONU MAC (owner 2026-10-17: the
 * PHP pages recharge/userdiagnose.php and recharge/onuchange.php are retired).
 *
 *   GET /access/operator/{ns}/subscribers/{AccountID}/diagnose        subscriberdevice:read
 *   PUT /access/operator/{ns}/subscribers/{AccountID}/devices/rename  subscriberdevice:write
 *
 * radaccess v1.112. The password never reaches the console: only whether one
 * exists and whether it is the device's own id.
 */
export interface DiagnoseRegistration {
  name: string;
  mobile: string;
  username: string;
  status: string;
  registered: string;
}

/** One active device. Times are as stored, "YYYY-MM-DD HH:MM:SS"; "" when none. */
export interface DiagnoseDevice {
  username: string;
  /** custuseracct.usertype: "primary" (the mobile), "onumac" (a wired ONU), ... */
  kind: string;
  credential: boolean;
  credential_is_device_id: boolean;
  plan: string;
  start: string;
  expiry: string;
  /** The plan's Expire-After in seconds, as text ("" when unset). */
  expireafter: string;
  /** The plan's fixed end, as the catalog writes it ("" when unset). */
  expiration: string;
  first_session: string;
  last_session: string;
  sessions: number;
  online: boolean;
}

/** One ONU change on the account (radaccess v1.114, owner 2026-10-17): technical history only. */
export interface DeviceChange {
  from: string;
  to: string;
  /** Database clock, "YYYY-MM-DD HH:MM:SS". */
  changed_at: string;
  /** The staff login that made it (its sign-in id), or the database login for old PHP changes. */
  changed_by: string;
  /** "console" (Change ONU MAC) or "audit" (an older change, copied from the audit trail). */
  source: string;
}

export interface SubscriberDiagnosis {
  registration: DiagnoseRegistration | null;
  devices: DiagnoseDevice[];
  /** Newest first. Absent from radaccess older than v1.114. */
  device_changes: DeviceChange[];
  /** The history table is not there yet: say so rather than "never changed". */
  history_unavailable: boolean;
  /** The database clock, the one the stored times were written by. */
  now: string;
}

const base = (ns: string, accountId: string) =>
  `/operator/${encodeURIComponent(ns)}/subscribers/${encodeURIComponent(accountId)}`;

export async function getDiagnosis(identity: CallerIdentity, ns: string, accountId: string, signal?: AbortSignal): Promise<SubscriberDiagnosis> {
  const r = await callGateway<SubscriberDiagnosis>(identity, config().gatewayBase, `${base(ns, accountId)}/diagnose`, signal ? { signal } : {});
  return {
    registration: r.data?.registration ?? null,
    devices: r.data?.devices ?? [],
    device_changes: r.data?.device_changes ?? [],
    // An older radaccess sends no history at all: as unknown as a missing table.
    history_unavailable: r.data?.history_unavailable ?? r.data?.device_changes === undefined,
    now: r.data?.now ?? "",
  };
}

export interface DeviceRenameResult {
  from: string;
  to: string;
  /** False when an earlier attempt had already moved sign-in to the new MAC. */
  signin_renamed: boolean;
  /** In User diagnose's ONU history (radaccess v1.114). Absent from older radaccess. */
  history_recorded?: boolean;
}

export async function renameDevice(identity: CallerIdentity, ns: string, accountId: string, from: string, to: string): Promise<DeviceRenameResult> {
  const r = await callGateway<DeviceRenameResult>(identity, config().gatewayBase, `${base(ns, accountId)}/devices/rename`, {
    method: "PUT",
    body: { from, to },
  });
  return r.data ?? { from, to: normalizeMac(to) ?? to, signin_renamed: true };
}

export const ONU = "onumac";

/**
 * The gateway's NormalizeMAC: colons, dashes, dots or none, either case, to
 * AA:BB:CC:DD:EE:FF. null for anything else and for all-zero / broadcast.
 */
export function normalizeMac(s: string): string | null {
  const hex = s.trim().replace(/[:.\-\s]/g, "").toUpperCase();
  if (!/^[0-9A-F]{12}$/.test(hex)) return null;
  const mac = hex.match(/../g)!.join(":");
  return mac === "00:00:00:00:00:00" || mac === "FF:FF:FF:FF:FF:FF" ? null : mac;
}

export type CheckLevel = "ok" | "problem" | "info";

export interface DiagnoseCheck {
  label: string;
  level: CheckLevel;
  detail: string;
}

const MAC_STORED = /^[0-9A-Fa-f]{2}(:[0-9A-Fa-f]{2}){5}$/;

/** "YYYY-MM-DD HH:MM:SS" to ms on one clock; NaN when empty or malformed. */
function at(s: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(s);
  return m ? Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +m[6]!) : NaN;
}

const DAY = 86_400_000;

function days(ms: number): string {
  const d = Math.round(ms / DAY);
  return d === 1 ? "1 day" : `${d} days`;
}

/** The subscriber-level checks: userdiagnose.php's name check. */
export function registrationChecks(d: SubscriberDiagnosis): DiagnoseCheck[] {
  const r = d.registration;
  if (!r) return [{ label: "Registration", level: "problem", detail: "No registration found for this account." }];
  const out: DiagnoseCheck[] = [];
  out.push(
    !r.name
      ? { label: "Name", level: "problem", detail: "Not recorded." }
      : NAME_CHARS.test(r.name)
        ? { label: "Name", level: "ok", detail: r.name }
        : { label: "Name", level: "problem", detail: `${r.name} — a name has only letters and spaces (also ' . , - ( )); remove: ${badNameChars(r.name)}` },
  );
  out.push(
    r.status === "disable"
      ? { label: "Registration", level: "problem", detail: "Disabled: this subscriber cannot sign in." }
      : { label: "Registration", level: "ok", detail: r.registered ? `Active since ${r.registered}` : "Active" },
  );
  return out;
}

/**
 * One device's checks, in userdiagnose.php's order: device id, password,
 * plan, dates, sessions, still active, and plan length. Pure: the clock is
 * the database's `now`.
 */
export function deviceChecks(dev: DiagnoseDevice, now: string): DiagnoseCheck[] {
  const out: DiagnoseCheck[] = [];
  const isOnu = dev.kind === ONU;

  if (/\s/.test(dev.username)) out.push({ label: "Device id", level: "problem", detail: `“${dev.username}” has a space in it.` });
  else if (isOnu && !MAC_STORED.test(dev.username)) out.push({ label: "Device id", level: "problem", detail: `${dev.username} is not a MAC address (AA:BB:CC:DD:EE:FF).` });
  else out.push({ label: "Device id", level: "ok", detail: dev.username });

  if (!dev.credential) out.push({ label: "Password", level: "problem", detail: "No sign-in password: this device cannot sign in." });
  else if (isOnu && !dev.credential_is_device_id) out.push({ label: "Password", level: "problem", detail: "Set, but not the device's own MAC, which an ONU signs in with." });
  else out.push({ label: "Password", level: "ok", detail: "Set" });

  if (!dev.plan) {
    out.push({ label: "Plan", level: "problem", detail: "No plan given to this device." });
    return out;
  }
  out.push({ label: "Plan", level: "ok", detail: dev.plan });

  const start = at(dev.start);
  const expiry = at(dev.expiry);
  const nowMs = at(now);
  out.push(Number.isNaN(start) ? { label: "Start", level: "problem", detail: "Not recorded." } : { label: "Start", level: "ok", detail: dev.start });
  out.push(Number.isNaN(expiry) ? { label: "Expiry", level: "problem", detail: "Not recorded." } : { label: "Expiry", level: "ok", detail: dev.expiry });

  if (dev.sessions === 0) {
    out.push({ label: "Sessions", level: "info", detail: "Never signed in." });
  } else {
    out.push({
      label: "Sessions",
      level: "ok",
      detail: `${dev.sessions} — first ${dev.first_session}, last ${dev.last_session}${dev.online ? " · online now" : ""}`,
    });
    const last = at(dev.last_session);
    if (!Number.isNaN(expiry) && !Number.isNaN(last) && last > expiry) {
      out.push({ label: "Last session", level: "problem", detail: `${dev.last_session} is after the plan expired (${dev.expiry}).` });
    }
  }

  if (!Number.isNaN(expiry) && !Number.isNaN(nowMs)) {
    out.push(
      nowMs <= expiry
        ? { label: "Plan active", level: "ok", detail: `Yes — ${days(expiry - nowMs)} left` }
        : { label: "Plan active", level: "problem", detail: `No — expired ${days(nowMs - expiry)} ago. Recharge to restore.` },
    );
  }

  const validity = Number(dev.expireafter);
  if (dev.expireafter && Number.isFinite(validity) && validity > 0 && !Number.isNaN(start) && !Number.isNaN(expiry)) {
    const off = Math.abs(expiry - start - validity * 1000);
    out.push(
      off <= DAY
        ? { label: "Plan length", level: "ok", detail: `${days(expiry - start)}, as the plan says` }
        : { label: "Plan length", level: "problem", detail: `Start to expiry is ${days(expiry - start)}; the plan gives ${days(validity * 1000)}.` },
    );
  }
  return out;
}

/** Who made a change, as staff read it: the signed-in user's own changes say "you". */
export function changedBy(c: DeviceChange, mySub: string | undefined): string {
  if (mySub && c.changed_by === mySub) return "you";
  if (c.source === "audit") return c.changed_by.includes("@") ? `${c.changed_by} (old PHP page)` : c.changed_by;
  return `staff ${c.changed_by}`;
}

/** The ONUs Change ONU MAC can work on. */
export function onuDevices(d: SubscriberDiagnosis): DiagnoseDevice[] {
  return d.devices.filter((x) => x.kind === ONU);
}
